import * as React from 'react';

import { OpenDialog } from '~teacher/components/quizzes/CopyMoveDialog/useCopyQuizToWorkspaceDialog';

import { uniqueIdFactory } from '../../../../../api/quiz/getQuizzes/normalize/uniqueIds';

interface LibraryFolderActions {
  openRenameFolder?: (uniqueId: string) => void;
  deleteFolder: (uniqueId: string) => void;
  openMoveDialog: OpenDialog;
}

const LibraryFolderActionContext =
  React.createContext<LibraryFolderActions>(undefined);

export interface LibraryFolderActionProviderProps {
  children: React.ReactNode;
  onOpenRenameFolder: (folderId: number) => void;
  onDeleteFolder: (folderId: number) => void;
  openMoveDialog: OpenDialog;
}

/**
 * This context provides folder actions such as rename/move/etc to prevent prop drilling
 */
export function LibraryFolderActionProvider({
  children,
  onOpenRenameFolder,
  onDeleteFolder,
  openMoveDialog,
}: LibraryFolderActionProviderProps) {
  const folderActions = React.useMemo(
    () => ({
      openRenameFolder: (uniqueId: string) => {
        onOpenRenameFolder(uniqueIdFactory.folder.toEntityId(uniqueId));
      },
      deleteFolder: (uniqueId: string) => {
        onDeleteFolder(uniqueIdFactory.folder.toEntityId(uniqueId));
      },
      openMoveDialog: openMoveDialog,
    }),
    [onOpenRenameFolder, onDeleteFolder, openMoveDialog]
  );

  return (
    <LibraryFolderActionContext.Provider value={folderActions}>
      {children}
    </LibraryFolderActionContext.Provider>
  );
}

export function useLibraryFolderActions() {
  const context = React.useContext(LibraryFolderActionContext);

  if (context === undefined) {
    throw new Error(
      'useLibraryFolderActions must be used within LibraryFolderActionContext'
    );
  }

  return context;
}
